import React, { useState, useEffect } from 'react'
import {
  Plus,
  Search,
  Filter,
  Edit2,
  Eye,
  Power,
  School,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Trophy,
  TrendingUp,
  Target,
  IndianRupee,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Building2,
  X,
  GripVertical,
  Info
} from 'lucide-react'
import { College, Agency } from '@/types'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Pagination from '@/components/ui/Pagination'
import ImageUpload from '@/components/common/ImageUpload'
import { formatDate, cn, isPresignedUrl } from '@/lib/utils'
import { api } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
]

// Define the pagination response interface for colleges
interface CollegePaginationResponse {
  items: College[]
  total: number
  page: number
  limit: number
  totalPages: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

const CollegeManagement: React.FC = () => {
  const [colleges, setColleges] = useState<College[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [filterLocation, setFilterLocation] = useState('')
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalColleges, setTotalColleges] = useState<number | null>(null)
  const [filteredTotal, setFilteredTotal] = useState(0)
  const [error, setError] = useState('')
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedCollege, setSelectedCollege] = useState<College | null>(null)

  const fetchColleges = async (
    page = 1,
    search = '',
    status = 'all',
    location = '',
    sort = '',
    order: 'ASC' | 'DESC' = 'ASC',
  ) => {
    try {
      setLoading(true)
      const params: any = {
        page,
        limit: 12,
      }

      if (search) params.search = search
      if (status !== 'all') params.isActive = status === 'active'
      if (location) params.location = location
      if (sort) {
        params.sortBy = sort
        params.sortOrder = order
      }

      console.log('Fetching colleges with params:', params)
      
      // Use centralized API call
      const response = await api.get<CollegePaginationResponse>(`/colleges?${new URLSearchParams(params)}`)
      console.log('College API response:', response)
      
      // The backend returns {items, total, page, limit, totalPages, etc.} structure
      setColleges(response.items || [])
      setTotalPages(response.totalPages || 1)
      setFilteredTotal(response.total || 0)
      setError('')
    } catch (error) {
      console.error('Failed to fetch colleges:', error)
      setError(`Failed to load institutions: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const fetchTotalCollegesCount = async () => {
    try {
      const response = await api.get<CollegePaginationResponse>('/colleges?page=1&limit=1')
      setTotalColleges(response.total ?? null)
    } catch {}
  }

  useEffect(() => {
    void fetchTotalCollegesCount()
  }, [])

  useEffect(() => {
    fetchColleges(currentPage, searchTerm, filterStatus, filterLocation, sortBy, sortOrder)
  }, [currentPage, searchTerm, filterStatus, filterLocation, sortBy, sortOrder])

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleFilterChange = (status: 'all' | 'active' | 'inactive') => {
    setFilterStatus(status)
    setCurrentPage(1)
  }

  const handleLocationChange = (value: string) => {
    setFilterLocation(value)
    setCurrentPage(1)
  }

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'))
    } else {
      setSortBy(column)
      setSortOrder('ASC')
    }
    setCurrentPage(1)
  }

  const handleAddCollege = () => {
    setSelectedCollege(null)
    setShowAddModal(true)
  }

  const handleEditCollege = async (college: College) => {
    // The list row doesn't include linked agencies — fetch the full detail
    // so the agency multi-select is pre-populated correctly.
    try {
      const full = await api.get<College>(`/colleges/${college.id}`)
      setSelectedCollege(full)
    } catch {
      setSelectedCollege(college)
    }
    setShowEditModal(true)
  }

  const handleViewCollege = async (college: College) => {
    try {
      const full = await api.get<College>(`/colleges/${college.id}`)
      setSelectedCollege(full)
    } catch {
      setSelectedCollege(college)
    }
    setShowViewModal(true)
  }

  const handleToggleStatus = async (college: College) => {
    const action = college.isActive ? 'deactivate' : 'activate'
    
    if (!confirm(`Are you sure you want to ${action} "${college.name}"?`)) {
      return
    }

    try {
      await api.patch(`/colleges/${college.id}`, { isActive: !college.isActive })
      // Refresh the list
      fetchColleges(currentPage, searchTerm, filterStatus, filterLocation, sortBy, sortOrder)
    } catch (error: any) {
      console.error('Failed to toggle college status:', error)
      setError(error.message || 'Failed to update institution status')
    }
  }

  return (
    <div className="gap-y-6 flex flex-col">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex flex-wrap items-center gap-x-2 gap-y-1">
          Institution Management
          {totalColleges !== null && (
            <span className="text-sm sm:text-base font-medium text-gray-400">
              ({totalColleges} Institutions)
            </span>
          )}
        </h1>
        <button
          onClick={handleAddCollege}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 flex items-center justify-center space-x-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Institution</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search institutions..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2 min-w-0">
            <Filter className="h-4 w-4 text-gray-500 shrink-0" />
            <select
              value={filterStatus}
              onChange={(e) => handleFilterChange(e.target.value as any)}
              className="w-full sm:w-auto min-w-0 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex items-center gap-2 min-w-0">
            <MapPin className="h-4 w-4 text-gray-500 shrink-0" />
            <select
              value={filterLocation}
              onChange={(e) => handleLocationChange(e.target.value)}
              className="w-full sm:w-auto min-w-0 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All Locations</option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {/* Colleges Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : colleges.length === 0 ? (
          <div className="p-12 text-center">
            <School className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No institutions found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterStatus !== 'all' || filterLocation
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first institution.'}
            </p>
            {(!searchTerm && filterStatus === 'all' && !filterLocation) && (
              <button
                onClick={handleAddCollege}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 inline-flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add First Institution</span>
              </button>
            )}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-200 md:hidden">
              {colleges.map((college) => (
                <li key={college.id} className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 h-10 w-10">
                      {college.logoUrl ? (
                        <img
                          className="h-10 w-10 rounded-lg object-cover"
                          src={college.logoUrl}
                          alt={college.name}
                        />
                      ) : (
                        <div className="h-10 w-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <School className="h-5 w-5 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{college.name}</p>
                      {college.website && (
                        <a
                          href={college.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-500 hover:text-primary flex items-center gap-1 truncate"
                        >
                          <Globe className="h-3 w-3 shrink-0" />
                          Website
                        </a>
                      )}
                    </div>
                    <span
                      className={cn(
                        'shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        college.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
                      )}
                    >
                      {college.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
                    {college.tier && (
                      <span
                        className={cn(
                          'inline-flex px-2 py-0.5 font-semibold rounded-full',
                          college.tier === 'tier_1' && 'bg-yellow-100 text-yellow-800',
                          college.tier === 'tier_2' && 'bg-blue-100 text-blue-800',
                          (college.tier === 'tier_3' || college.tier === 'tier_4') &&
                            'bg-gray-100 text-gray-800',
                        )}
                      >
                        {college.tier.replace('tier_', 'Tier ')}
                      </span>
                    )}
                    {college.nirfRank && (
                      <span className="flex items-center gap-1">
                        <Trophy className="h-3 w-3 text-yellow-600" />
                        NIRF #{college.nirfRank}
                      </span>
                    )}
                    {(college.air ?? 0) > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 font-semibold rounded-full bg-primary/10 text-primary">
                        AIR {college.air}
                      </span>
                    )}
                    {(college.location || [college.city, college.state].filter(Boolean).join(", ")) && (
                      <span className="flex items-center gap-1 truncate">
                        <MapPin className="h-3 w-3 text-gray-400 shrink-0" />
                        {(college.location || [college.city, college.state].filter(Boolean).join(", "))}
                      </span>
                    )}
                    {college.metadata?.type && <span>{college.metadata.type}</span>}
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      onClick={() => handleViewCollege(college)}
                      className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
                      aria-label="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditCollege(college)}
                      className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50"
                      aria-label="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(college)}
                      className={cn(
                        'p-1.5 rounded-md',
                        college.isActive
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-green-600 hover:bg-green-50',
                      )}
                      aria-label={college.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institution
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tier / NIRF
                    </th>
                    <th
                      onClick={() => handleSort('air')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                    >
                      <span className="inline-flex items-center gap-1">
                        AIR
                        {sortBy === 'air' ? (
                          sortOrder === 'ASC' ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowDown className="h-3.5 w-3.5" />
                          )
                        ) : (
                          <ArrowUpDown className="h-3.5 w-3.5 text-gray-300" />
                        )}
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort('location')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                    >
                      <span className="inline-flex items-center gap-1">
                        Location
                        {sortBy === 'location' ? (
                          sortOrder === 'ASC' ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowDown className="h-3.5 w-3.5" />
                          )
                        ) : (
                          <ArrowUpDown className="h-3.5 w-3.5 text-gray-300" />
                        )}
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {colleges.map((college) => (
                    <tr key={college.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {college.logoUrl ? (
                              <img 
                                className="h-10 w-10 rounded-lg object-cover" 
                                src={college.logoUrl} 
                                alt={college.name}
                              />
                            ) : (
                              <div className="h-10 w-10 bg-blue-500 rounded-lg flex items-center justify-center">
                                <School className="h-5 w-5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {college.name}
                            </div>
                            {college.website && (
                              <div className="text-sm text-gray-500 flex items-center">
                                <Globe className="h-3 w-3 mr-1" />
                                <a 
                                  href={college.website} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="hover:text-primary"
                                >
                                  Website
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {college.tier && (
                            <span className={cn(
                              'inline-flex px-2 py-0.5 text-xs font-semibold rounded-full',
                              college.tier === 'tier_1' && 'bg-yellow-100 text-yellow-800',
                              college.tier === 'tier_2' && 'bg-blue-100 text-blue-800',
                              (college.tier === 'tier_3' || college.tier === 'tier_4') && 'bg-gray-100 text-gray-800'
                            )}>
                              {college.tier.replace('tier_', 'Tier ')}
                            </span>
                          )}
                          {college.nirfRank && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Trophy className="h-3 w-3 mr-1 text-yellow-600" />
                              <span>NIRF #{college.nirfRank}</span>
                            </div>
                          )}
                          {!college.tier && !college.nirfRank && (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(college.air ?? 0) > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-primary/10 text-primary">
                            {college.air}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(college.location || [college.city, college.state].filter(Boolean).join(", ")) ? (
                            <div className="flex items-center">
                              <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                              <span className="truncate max-w-[200px]">{(college.location || [college.city, college.state].filter(Boolean).join(", "))}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {college.metadata?.type || 'N/A'}
                        </div>
                        {college.metadata?.founded && (
                          <div className="text-sm text-gray-500 flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            Founded {college.metadata.founded}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewCollege(college)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditCollege(college)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(college)}
                            className={cn(
                              'p-1 rounded',
                              college.isActive 
                                ? 'text-red-600 hover:text-red-900' 
                                : 'text-green-600 hover:text-green-900'
                            )}
                            title={college.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <Power className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                total={filteredTotal}
                pageSize={12}
                itemLabel="institutions"
              />
            </div>
          </>
        )}
      </div>

      {/* Add College Modal */}
      {showAddModal && (
        <CollegeModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false)
            fetchColleges(currentPage, searchTerm, filterStatus, filterLocation, sortBy, sortOrder)
            void fetchTotalCollegesCount()
          }}
          title="Add New Institution"
        />
      )}

      {/* Edit College Modal */}
      {showEditModal && selectedCollege && (
        <CollegeModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false)
            fetchColleges(currentPage, searchTerm, filterStatus, filterLocation, sortBy, sortOrder)
          }}
          college={selectedCollege}
          title="Edit Institution"
        />
      )}

      {/* View College Modal */}
      {showViewModal && selectedCollege && (
        <CollegeViewModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          college={selectedCollege}
        />
      )}
    </div>
  )
}

// College Form Modal Component
interface CollegeModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  college?: College
  title: string
}

const CollegeModal: React.FC<CollegeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  college,
  title
}) => {
  const { isSuperAdmin } = useAuth()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    state: '',
    university: '',
    website: '',
    email: '',
    phone: '',
    logoUrl: '',
    imageUrl: '',
    isActive: true,
    // New fields
    tier: '' as '' | 'tier_1' | 'tier_2' | 'tier_3' | 'tier_4',
    nirfRank: undefined as number | undefined,
    nirfCategory: '',
    acceptanceRate: undefined as number | undefined,
    minPercentile: undefined as number | undefined,
    avgPackage: undefined as number | undefined,
    highestPackage: undefined as number | undefined,
    insightsPros: '',
    insightsCons: '',
    insightsRecommendation: '',
    insightsNotRecommendedIf: '',
    insightsContextNote: '',
    insightsVerdict: '',
    verdictScore: undefined as number | undefined,
    dataConfidence: '',
    metadata: {
      founded: undefined as number | undefined,
      type: '',
      acceptanceRate: undefined as number | undefined,
      studentPopulation: undefined as number | undefined,
      facultyCount: undefined as number | undefined,
      campusSize: undefined as number | undefined
    },
    agencyIds: [] as string[],
    air: 0
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const [agencies, setAgencies] = useState<Agency[]>([])
  const [draggedAgencyIndex, setDraggedAgencyIndex] = useState<number | null>(null)

  const reorderAgency = (from: number, to: number) => {
    if (to < 0 || to >= formData.agencyIds.length || from === to) return
    const next = [...formData.agencyIds]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setFormData({ ...formData, agencyIds: next })
  }

  // Load available agencies to link, once, whenever the modal opens
  useEffect(() => {
    if (!isOpen) return
    api
      .get<{ data: Agency[]; meta: any }>(`/admin/agencies?limit=100`)
      .then((res) => setAgencies(res.data ?? []))
      .catch(() => setAgencies([]))
  }, [isOpen])

  // Initialize form data when college is provided (edit mode)
  useEffect(() => {
    if (college) {
      setFormData({
        name: college.name || '',
        description: college.description || '',
        location: college.location || '',
        state: college.state || '',
        university: college.university || '',
        website: college.website || '',
        email: college.email || '',
        phone: college.phone || '',
        logoUrl: isPresignedUrl(college.logoUrl) ? '' : (college.logoUrl || ''),
        imageUrl: isPresignedUrl(college.imageUrl) ? '' : (college.imageUrl || ''),
        isActive: college.isActive ?? true,
        // New fields
        tier: college.tier || '',
        nirfRank: college.nirfRank,
        nirfCategory: college.nirfCategory || '',
        acceptanceRate: college.acceptanceRate,
        minPercentile: college.minPercentile,
        avgPackage: college.avgPackage,
        highestPackage: college.highestPackage,
        insightsPros: college.insightsPros || '',
        insightsCons: college.insightsCons || '',
        insightsRecommendation: college.insightsRecommendation || '',
        insightsNotRecommendedIf: college.insightsNotRecommendedIf || '',
        insightsContextNote: college.insightsContextNote || '',
        insightsVerdict: college.insightsVerdict || '',
        verdictScore: college.verdictScore,
        dataConfidence: college.dataConfidence || '',
        metadata: {
          founded: college.metadata?.founded,
          type: college.metadata?.type || '',
          acceptanceRate: college.metadata?.acceptanceRate,
          studentPopulation: college.metadata?.studentPopulation,
          facultyCount: college.metadata?.facultyCount,
          campusSize: college.metadata?.campusSize
        },
        agencyIds: college.agencies?.map((a) => a.id) || [],
        air: college.air ?? 0
      })
    }
  }, [college])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setFormError('')

    try {
      // Prepare the data in the format expected by the backend
      // Only include fields that are defined in BaseCollegeDto
      const submitData: any = {
        name: formData.name,
        description: formData.description || undefined,
        location: formData.location || undefined,
        state: formData.state || undefined,
        university: formData.university || undefined,
        website: formData.website || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        logoUrl: formData.logoUrl || undefined,
        imageUrl: formData.imageUrl || undefined,
        isActive: formData.isActive,
        // New fields
        tier: formData.tier || undefined,
        nirfRank: formData.nirfRank || undefined,
        nirfCategory: formData.nirfCategory || undefined,
        acceptanceRate: formData.acceptanceRate || undefined,
        minPercentile: formData.minPercentile || undefined,
        avgPackage: formData.avgPackage || undefined,
        highestPackage: formData.highestPackage || undefined,
        insightsPros: formData.insightsPros || undefined,
        insightsCons: formData.insightsCons || undefined,
        insightsRecommendation: formData.insightsRecommendation || undefined,
        insightsNotRecommendedIf: formData.insightsNotRecommendedIf || undefined,
        insightsContextNote: formData.insightsContextNote || undefined,
        insightsVerdict: formData.insightsVerdict || undefined,
        verdictScore: formData.verdictScore ?? undefined,
        dataConfidence: formData.dataConfidence || undefined,
        agencyIds: formData.agencyIds,
        // Keep metadata keys this form doesn't edit (e.g. collegeType, rankingNotes);
        // the API replaces metadata wholesale.
        metadata: {
          ...(college?.metadata ?? {}),
          founded: formData.metadata.founded || undefined,
          type: formData.metadata.type || undefined,
          acceptanceRate: formData.metadata.acceptanceRate || undefined,
          studentPopulation: formData.metadata.studentPopulation || undefined,
          facultyCount: formData.metadata.facultyCount || undefined,
          campusSize: formData.metadata.campusSize || undefined
        }
      }

      // Clean up undefined values from metadata
      Object.keys(submitData.metadata).forEach(key => {
        if (submitData.metadata[key] === undefined) {
          delete submitData.metadata[key]
        }
      })

      // If metadata is empty, remove it
      if (Object.keys(submitData.metadata).length === 0) {
        delete submitData.metadata
      }
      let collegeId = college?.id

      if (college) {
        await api.patch(`/colleges/${college.id}`, submitData)
      } else {
        const created = await api.post<any>('/colleges', submitData)
        collegeId = created?.id ?? created?.data?.id
      }

      const airChanged = (college?.air ?? 0) !== formData.air
      if (isSuperAdmin && collegeId && (college ? airChanged : formData.air > 0)) {
        await api.patch(`/colleges/${collegeId}/air`, { air: formData.air })
      }

      onSuccess()
    } catch (error: any) {
      setFormError(error.message || 'Failed to save institution')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Modal Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Modal Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <div className="text-red-800 text-sm">{formError}</div>
            </div>
          )}

          <form id="college-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Institution Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <select
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select State</option>
                <option value="Andhra Pradesh">Andhra Pradesh</option>
                <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                <option value="Assam">Assam</option>
                <option value="Bihar">Bihar</option>
                <option value="Chhattisgarh">Chhattisgarh</option>
                <option value="Goa">Goa</option>
                <option value="Gujarat">Gujarat</option>
                <option value="Haryana">Haryana</option>
                <option value="Himachal Pradesh">Himachal Pradesh</option>
                <option value="Jharkhand">Jharkhand</option>
                <option value="Karnataka">Karnataka</option>
                <option value="Kerala">Kerala</option>
                <option value="Madhya Pradesh">Madhya Pradesh</option>
                <option value="Maharashtra">Maharashtra</option>
                <option value="Manipur">Manipur</option>
                <option value="Meghalaya">Meghalaya</option>
                <option value="Mizoram">Mizoram</option>
                <option value="Nagaland">Nagaland</option>
                <option value="Odisha">Odisha</option>
                <option value="Punjab">Punjab</option>
                <option value="Rajasthan">Rajasthan</option>
                <option value="Sikkim">Sikkim</option>
                <option value="Tamil Nadu">Tamil Nadu</option>
                <option value="Telangana">Telangana</option>
                <option value="Tripura">Tripura</option>
                <option value="Uttar Pradesh">Uttar Pradesh</option>
                <option value="Uttarakhand">Uttarakhand</option>
                <option value="West Bengal">West Bengal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">University / Affiliation</label>
              <input
                type="text"
                value={formData.university}
                onChange={(e) => setFormData({ ...formData, university: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="rounded-lg border border-gray-200 p-4 space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-800">Insights</h4>
                <p className="text-xs text-gray-500">
                  Internal research notes the AI counselor uses to judge fit. One point per line.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Verdict score (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.verdictScore ?? ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      verdictScore: e.target.value !== '' ? parseInt(e.target.value) : undefined
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data confidence</label>
                  <input
                    type="text"
                    maxLength={30}
                    placeholder="e.g. LOW, MODERATE, HIGH"
                    value={formData.dataConfidence}
                    onChange={(e) => setFormData({ ...formData, dataConfidence: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Verdict</label>
              <textarea
                value={formData.insightsVerdict}
                onChange={(e) => setFormData({ ...formData, insightsVerdict: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pros</label>
              <textarea
                value={formData.insightsPros}
                onChange={(e) => setFormData({ ...formData, insightsPros: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cons</label>
              <textarea
                value={formData.insightsCons}
                onChange={(e) => setFormData({ ...formData, insightsCons: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recommendation</label>
              <textarea
                value={formData.insightsRecommendation}
                onChange={(e) => setFormData({ ...formData, insightsRecommendation: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Not recommended if</label>
              <textarea
                value={formData.insightsNotRecommendedIf}
                onChange={(e) => setFormData({ ...formData, insightsNotRecommendedIf: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Context note</label>
              <textarea
                value={formData.insightsContextNote}
                onChange={(e) => setFormData({ ...formData, insightsContextNote: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="text"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ImageUpload
                label="Institution Logo"
                value={formData.logoUrl}
                onChange={(url) => setFormData({ ...formData, logoUrl: url })}
                onUploadError={(error) => setFormError(`Logo upload failed: ${error}`)}
                maxSizeMB={5}
                previewUrl={college?.logoUrl}
              />

              <ImageUpload
                label="Institution Image"
                value={formData.imageUrl}
                onChange={(url) => setFormData({ ...formData, imageUrl: url })}
                onUploadError={(error) => setFormError(`Image upload failed: ${error}`)}
                maxSizeMB={10}
                placeholder="Upload campus image"
                previewUrl={college?.imageUrl}
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Ranking & Performance</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institution Tier
                  </label>
                  <select
                    value={formData.tier}
                    onChange={(e) => setFormData({
                      ...formData,
                      tier: e.target.value as any
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select Tier</option>
                    <option value="tier_1">Tier 1 (Top - IITs, NITs, AIIMS)</option>
                    <option value="tier_2">Tier 2 (Good state/private)</option>
                    <option value="tier_3">Tier 3 (Regional)</option>
                    <option value="tier_4">Tier 4 (Local)</option>
                  </select>
                </div>
                {isSuperAdmin && (
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                      AI Recommendation Rank (AIR)
                      <span
                        title="How strongly the AI counselor should lean toward suggesting this institution among similar fits. 0 is neutral (no steer); 4 is the strongest push. It never filters an institution out."
                        className="text-gray-400 hover:text-gray-600 cursor-help"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </span>
                    </label>
                    <select
                      value={formData.air}
                      onChange={(e) => setFormData({
                        ...formData,
                        air: parseInt(e.target.value)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {Object.entries(AIR_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    NIRF Rank
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.nirfRank || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      nirfRank: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    NIRF Category
                  </label>
                  <select
                    value={formData.nirfCategory}
                    onChange={(e) => setFormData({
                      ...formData,
                      nirfCategory: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select Category</option>
                    <option value="Overall">Overall</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Management">Management</option>
                    <option value="Medical">Medical</option>
                    <option value="Law">Law</option>
                    <option value="Pharmacy">Pharmacy</option>
                    <option value="Architecture">Architecture</option>
                    <option value="University">University</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Acceptance Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.acceptanceRate || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      acceptanceRate: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Percentile Required
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.minPercentile || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      minPercentile: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Avg Package (₹ Lakhs)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.avgPackage || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      avgPackage: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Highest Package (₹ Lakhs)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.highestPackage || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      highestPackage: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Founded Year
                  </label>
                  <input
                    type="number"
                    value={formData.metadata.founded || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      metadata: {
                        ...formData.metadata,
                        founded: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={formData.metadata.type}
                    onChange={(e) => setFormData({
                      ...formData,
                      metadata: {
                        ...formData.metadata,
                        type: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select Type</option>
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                    <option value="Private / autonomous business school">Private / autonomous business school</option>
                    <option value="Private business school">Private business school</option>
                    <option value="Private university">Private university</option>
                    <option value="Private management institute">Private management institute</option>
                    <option value="Private / autonomous institute">Private / autonomous institute</option>
                    <option value="Private business school (AICTE approved)">Private business school (AICTE approved)</option>
                    <option value="Private, affiliated">Private, affiliated with Dr. A.P.J. Abdul Kalam Technical University, UP; AICTE approved</option>
                    <option value="Private / autonomous B-School">Private / autonomous B-School</option>
                    <option value="Private institute (part of IILM group)">Private institute (part of IILM group)</option>
                    <option value="Private institution">Private institution</option>
                    <option value="Private institute (engineering & management)">Private institute (engineering & management)</option>
                    <option value="Private technical & management group">Private technical & management group</option>
                    <option value="Private business institute">Private business institute</option>
                    <option value="Private management and technical group">Private management and technical group</option>
                    <option value="Private group">Private group (engineering, management, pharmacy etc.)</option>
                    <option value="Deemed-to-be University">Deemed-to-be University</option>
                    <option value="Private (affiliated to Bangalore Univ.)">Private (affiliated to Bangalore Univ.)</option>
                    <option value="Private (AICTE approved)">Private (AICTE approved)</option>
                    <option value="Private (part of DY Patil Group)">Private (part of DY Patil Group)</option>
                    <option value="Private (affiliated to SPPU)">Private (affiliated to SPPU)</option>
                    <option value="Private (PGDM)">Private (PGDM)</option>
                    <option value="Private (under PCET)">Private (under PCET)</option>
                    <option value="Private (varies by campus)">Private (varies by campus)</option>
                    <option value="Private (AICTE approved, AKTU/CCSU affiliated)">Private (AICTE approved, AKTU/CCSU affiliated)</option>
                    <option value="Community">Community</option>
                    <option value="Technical">Technical</option>
                  </select>
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Acceptance Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.metadata.acceptanceRate || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      metadata: {
                        ...formData.metadata,
                        acceptanceRate: e.target.value ? parseFloat(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div> */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Student Population
                  </label>
                  <input
                    type="number"
                    value={formData.metadata.studentPopulation || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      metadata: {
                        ...formData.metadata,
                        studentPopulation: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Faculty Count
                  </label>
                  <input
                    type="number"
                    value={formData.metadata.facultyCount || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      metadata: {
                        ...formData.metadata,
                        facultyCount: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campus Size (acres)
                  </label>
                  <input
                    type="number"
                    value={formData.metadata.campusSize || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      metadata: {
                        ...formData.metadata,
                        campusSize: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h3 className="text-sm font-semibold text-gray-800">Linked Agencies</h3>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">
                Only active agencies can be newly linked. Order matters — the top agency has the highest priority for this institution.
              </p>

              {formData.agencyIds.length > 0 && (
                <div className="mb-3 border border-gray-300 rounded-md divide-y divide-gray-100 bg-white overflow-hidden">
                  {formData.agencyIds.map((agencyId, index) => {
                    const agency = agencies.find((a) => a.id === agencyId)
                    const isInactive = agency && agency.status !== 'ACTIVE'
                    return (
                      <div
                        key={agencyId}
                        draggable
                        onDragStart={() => setDraggedAgencyIndex(index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault()
                          if (draggedAgencyIndex !== null) reorderAgency(draggedAgencyIndex, index)
                          setDraggedAgencyIndex(null)
                        }}
                        onDragEnd={() => setDraggedAgencyIndex(null)}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 text-sm cursor-grab active:cursor-grabbing",
                          draggedAgencyIndex === index && "opacity-40"
                        )}
                      >
                        <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0" />
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                          {index + 1}
                        </span>
                        <span className="flex-1 text-gray-900 truncate">
                          {agency?.name ?? 'Unknown agency'}
                        </span>
                        {isInactive && (
                          <span className="text-xs text-red-500">({agency.status})</span>
                        )}
                        <button
                          type="button"
                          onClick={() => reorderAgency(index, index - 1)}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => reorderAgency(index, index + 1)}
                          disabled={index === formData.agencyIds.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              agencyIds: formData.agencyIds.filter((id) => id !== agencyId)
                            })
                          }}
                          className="p-1 text-gray-400 hover:text-red-600"
                          aria-label="Remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}

              {(() => {
                const availableToAdd = agencies.filter(
                  (a) => a.status === 'ACTIVE' && !formData.agencyIds.includes(a.id)
                )
                if (agencies.length === 0) {
                  return <p className="text-xs text-gray-400">No agencies available yet.</p>
                }
                if (availableToAdd.length === 0) {
                  return <p className="text-xs text-gray-400">No more active agencies to add.</p>
                }
                return (
                  <select
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return
                      setFormData({ ...formData, agencyIds: [...formData.agencyIds, e.target.value] })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">+ Add an agency...</option>
                    {availableToAdd.map((agency) => (
                      <option key={agency.id} value={agency.id}>{agency.name}</option>
                    ))}
                  </select>
                )
              })()}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                Institution is Active
              </label>
            </div>
          </form>
        </div>

        {/* Modal Footer - Fixed */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="college-form"
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : college ? 'Update Institution' : 'Create Institution'}
          </button>
        </div>
      </div>
    </div>
  )
}

// College View Modal Component
interface CollegeViewModalProps {
  isOpen: boolean
  onClose: () => void
  college: College
}

const AIR_LABELS: Record<number, string> = {
  0: '0 - Neutral',
  1: '1 - Lowest',
  2: '2 - Low',
  3: '3 - High',
  4: '4 - Highest',
}

const CollegeViewModal: React.FC<CollegeViewModalProps> = ({
  isOpen,
  onClose,
  college
}) => {
  const air = college.air ?? 0

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Modal Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Institution Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Modal Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-blue-500 rounded-lg flex items-center justify-center">
                {college.logoUrl ? (
                  <img 
                    src={college.logoUrl} 
                    alt={college.name}
                    className="h-16 w-16 rounded-lg object-cover"
                  />
                ) : (
                  <School className="h-8 w-8 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{college.name}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    college.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  )}>
                    {college.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {college.metadata?.type && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {college.metadata.type}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                AI Recommendation (AIR)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                How strongly the AI counselor should lean toward suggesting this institution. 0 is neutral (no steer); 4 is the strongest push.
              </p>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {AIR_LABELS[air] ?? air}
              </span>
            </div>

            {college.imageUrl && (
              <div>
                <img
                  src={college.imageUrl}
                  alt={`${college.name} campus`}
                  className="w-full h-48 object-cover rounded-lg"
                />
              </div>
            )}

            {(college.university || college.verdictScore != null || college.dataConfidence || college.insightsVerdict || college.insightsPros || college.insightsCons || college.insightsRecommendation || college.insightsNotRecommendedIf || college.insightsContextNote || college.metadata?.rankingNotes) && (
              <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                {college.university && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">University / Affiliation</label>
                    <p className="text-sm text-gray-900">{college.university}</p>
                  </div>
                )}
                {(college.verdictScore != null || college.dataConfidence) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {college.verdictScore != null && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        Verdict score: {college.verdictScore}/100
                      </span>
                    )}
                    {college.dataConfidence && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Data confidence: {college.dataConfidence}
                      </span>
                    )}
                  </div>
                )}
                {([
                  ['Verdict', college.insightsVerdict],
                  ['Pros', college.insightsPros],
                  ['Cons', college.insightsCons],
                  ['Recommendation', college.insightsRecommendation],
                  ['Not recommended if', college.insightsNotRecommendedIf],
                  ['Context note', college.insightsContextNote],
                  ['Ranking / accreditation notes', college.metadata?.rankingNotes],
                ] as Array<[string, string | undefined]>).map(([label, text]) =>
                  text ? (
                    <div key={label}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                      <p className="text-sm text-gray-900 whitespace-pre-line">{text}</p>
                    </div>
                  ) : null
                )}
              </div>
            )}

            {college.description && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-sm text-gray-900">{college.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <p className="text-sm text-gray-900 flex items-center">
                  {(college.location || [college.city, college.state].filter(Boolean).join(", ")) ? (
                    <>
                      <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                      {(college.location || [college.city, college.state].filter(Boolean).join(", "))}
                    </>
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                {college.website ? (
                  <a 
                    href={college.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center"
                  >
                    <Globe className="h-3 w-3 mr-1" />
                    {college.website}
                  </a>
                ) : (
                  <p className="text-sm text-gray-900">N/A</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="text-sm text-gray-900 flex items-center">
                  {college.email ? (
                    <>
                      <Mail className="h-3 w-3 mr-1 text-gray-400" />
                      {college.email}
                    </>
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <p className="text-sm text-gray-900 flex items-center">
                  {college.phone ? (
                    <>
                      <Phone className="h-3 w-3 mr-1 text-gray-400" />
                      {college.phone}
                    </>
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>
            </div>

            {college.state && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <p className="text-sm text-gray-900 flex items-center">
                  <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                  {college.state}
                </p>
              </div>
            )}

            {/* Ranking & Performance Section */}
            {(college.tier || college.nirfRank || college.acceptanceRate || college.minPercentile ||
              college.avgPackage || college.highestPackage) && (
              <div className="border-t pt-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Ranking & Performance</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {college.tier && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <span className={cn(
                        'inline-flex px-3 py-1 text-sm font-semibold rounded-full',
                        college.tier === 'tier_1' && 'bg-yellow-100 text-yellow-800',
                        college.tier === 'tier_2' && 'bg-blue-100 text-blue-800',
                        (college.tier === 'tier_3' || college.tier === 'tier_4') && 'bg-gray-100 text-gray-800'
                      )}>
                        {college.tier.replace('tier_', 'Tier ')}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">Institution Tier</div>
                    </div>
                  )}
                  {college.nirfRank && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-center">
                        <Trophy className="h-5 w-5 text-yellow-600 mr-1" />
                        <span className="text-2xl font-semibold text-gray-900">#{college.nirfRank}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        NIRF Rank{college.nirfCategory && ` - ${college.nirfCategory}`}
                      </div>
                    </div>
                  )}
                  {college.acceptanceRate && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-semibold text-gray-900">{college.acceptanceRate}%</div>
                      <div className="text-xs text-gray-500 mt-1">Acceptance Rate</div>
                    </div>
                  )}
                  {college.minPercentile && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-center">
                        <Target className="h-4 w-4 text-blue-600 mr-1" />
                        <span className="text-2xl font-semibold text-gray-900">{college.minPercentile}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Min Percentile</div>
                    </div>
                  )}
                  {college.avgPackage && (
                    <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-center">
                        <IndianRupee className="h-4 w-4 text-green-600 mr-0.5" />
                        <span className="text-2xl font-semibold text-gray-900">{college.avgPackage}</span>
                        <span className="text-sm text-gray-600 ml-1">L</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Avg Package</div>
                    </div>
                  )}
                  {college.highestPackage && (
                    <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                        <IndianRupee className="h-4 w-4 text-green-600 mr-0.5" />
                        <span className="text-2xl font-semibold text-gray-900">{college.highestPackage}</span>
                        <span className="text-sm text-gray-600 ml-1">L</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Highest Package</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {college.metadata && (
              <div className="border-t pt-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {college.metadata.founded && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-semibold text-gray-900">
                        {college.metadata.founded}
                      </div>
                      <div className="text-sm text-gray-500">Founded</div>
                    </div>
                  )}
                  {college.metadata.acceptanceRate && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-semibold text-gray-900">
                        {college.metadata.acceptanceRate}%
                      </div>
                      <div className="text-sm text-gray-500">Acceptance Rate</div>
                    </div>
                  )}
                  {college.metadata.studentPopulation && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-semibold text-gray-900">
                        {college.metadata.studentPopulation.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Students</div>
                    </div>
                  )}
                  {college.metadata.facultyCount && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-semibold text-gray-900">
                        {college.metadata.facultyCount.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Faculty</div>
                    </div>
                  )}
                  {college.metadata.campusSize && (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-semibold text-gray-900">
                        {college.metadata.campusSize.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">Acres</div>
                    </div>
                  )}
                </div>

                {college.metadata.rankings && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Rankings</h5>
                    <div className="space-y-2">
                      {college.metadata.rankings.national && (
                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-600">National Ranking</span>
                          <span className="text-sm font-semibold text-gray-900">#{college.metadata.rankings.national}</span>
                        </div>
                      )}
                      {college.metadata.rankings.global && (
                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-600">Global Ranking</span>
                          <span className="text-sm font-semibold text-gray-900">#{college.metadata.rankings.global}</span>
                        </div>
                      )}
                      {college.metadata.rankings.subjects && college.metadata.rankings.subjects.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-gray-700 mb-1">Subject Rankings</p>
                          <div className="space-y-1">
                            {college.metadata.rankings.subjects.map((subject, index) => (
                              <div key={index} className="flex items-center justify-between p-1 text-sm">
                                <span className="text-gray-600">{subject.name}</span>
                                <span className="font-medium text-gray-900">#{subject.rank}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                <Building2 className="h-4 w-4" />
                Linked Agencies
              </h4>
              {college.agencies && college.agencies.length > 0 ? (
                <div className="border border-gray-200 rounded-md divide-y divide-gray-100 bg-white overflow-hidden">
                  {college.agencies.map((agency, index) => (
                    <div key={agency.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-gray-900">{agency.name}</span>
                      {agency.status !== 'ACTIVE' && (
                        <span className="text-xs text-red-500">({agency.status})</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No agencies linked yet.</p>
              )}
            </div>

            <div className="text-sm text-gray-500 pt-4 border-t border-gray-200">
              <p>Created: {formatDate(college.createdAt)}</p>
              <p>Updated: {formatDate(college.updatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Modal Footer - Fixed */}
        <div className="flex justify-end p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default CollegeManagement